import { isExportMode } from './canvas-render-mode'

/**
 * What the PDF capture waits for (canvas load 1.6). While the page renders in export mode, work that changes what
 * the export shows registers here: element and variable fetches, signature positions, QR codes, images.
 * SpacePagesPrintMode waits for it to settle before it sets `window.__PDF_READY__`. If any of it failed, it lists the
 * failed work in `window.__PDF_ERROR__` instead and leaves `__PDF_READY__` false, so the PDF service fails the job
 * instead of capturing a partial PDF.
 *
 * Only labels are recorded, never content or URLs. Outside export mode tracking does nothing.
 */

/**
 * Longest the export waits for tracked work in total. The PDF service waits up to its `pdf.renderTimeoutMs`
 * (30s by default, 180s in its production config).
 */
export const EXPORT_SETTLE_TIMEOUT_MS = 20_000
/** Extra settle time per space page when many parallel element fetches run (export only). */
export const EXPORT_SETTLE_PER_PAGE_MS = 5_000
/** Cap aligned with PDF service `pdf.renderTimeoutMs` in production. */
export const EXPORT_SETTLE_MAX_MS = 180_000
/** Longest one image may take to decode, so a slow image cannot hold the export. */
export const IMAGE_DECODE_TIMEOUT_MS = 5_000

export function exportSettleTimeoutMs(pageCount: number): number {
  const pages = Math.max(1, pageCount)
  return Math.min(EXPORT_SETTLE_MAX_MS, EXPORT_SETTLE_TIMEOUT_MS + pages * EXPORT_SETTLE_PER_PAGE_MS)
}

export type ExportSettled = {
  /** Labels of work that failed or was still running at the deadline, each once */
  failed: string[]
}

type Task = { label: string; bestEffort: boolean }

// Replaced on reset, so work from an export that is gone cannot touch the next one
let pending = new Set<Task>()
let failed = new Set<string>()
let deadlineAt: number | null = null
const listeners = new Set<() => void>()

/**
 * Registers work the export must wait for; returns `task` unchanged. Its rejection counts as handled.
 * `bestEffort` work is waited for but never reported as failed, even when still running at the deadline.
 */
export function trackExportTask<T>(
  label: string,
  task: Promise<T>,
  { bestEffort = false }: { bestEffort?: boolean } = {}
): Promise<T> {
  if (!isExportMode()) return task
  const entry: Task = { label, bestEffort }
  const tasks = pending
  const failures = failed
  tasks.add(entry)
  const done = (didFail: boolean) => {
    // Removed at the deadline (already counted as failed), or by a reset
    if (!tasks.delete(entry)) return
    if (didFail && !bestEffort) failures.add(label)
    listeners.forEach(listener => listener())
  }
  task.then(
    () => done(false),
    () => done(true)
  )
  return task
}

/**
 * Resolves once no tracked work is left, and none was started by the work that settled last (rendering after a
 * fetch can start more: an async component, its QR code). Work still running at the deadline, counted from the first
 * call, is reported as failed (unless best effort) and no longer waited for. `restartDeadline` gives a later phase
 * (work started by rendering) a full window of its own instead of what the fetches left of the shared one.
 */
export function whenExportSettled({
  restartDeadline = false,
  settleTimeoutMs
}: { restartDeadline?: boolean; settleTimeoutMs?: number } = {}): Promise<ExportSettled> {
  if (deadlineAt === null || restartDeadline) {
    deadlineAt = Date.now() + (settleTimeoutMs ?? EXPORT_SETTLE_TIMEOUT_MS)
  }
  const tasks = pending
  return new Promise(resolve => {
    let quietTimer: ReturnType<typeof setTimeout> | null = null
    const finish = () => {
      clearTimeout(deadlineTimer)
      if (quietTimer !== null) clearTimeout(quietTimer)
      listeners.delete(check)
      if (tasks === pending) {
        tasks.forEach(task => {
          if (!task.bestEffort) failed.add(task.label)
        })
        tasks.clear()
      }
      resolve({ failed: tasks === pending ? Array.from(failed) : [] })
    }
    function check() {
      if (quietTimer !== null) clearTimeout(quietTimer)
      quietTimer = null
      if (tasks !== pending) return finish()
      if (tasks.size > 0) return
      // Promise callbacks run before a timer, so work started while settling the last task is registered by then
      quietTimer = setTimeout(() => {
        quietTimer = null
        if (tasks.size === 0 || tasks !== pending) finish()
      }, 0)
    }
    const deadlineTimer = setTimeout(finish, Math.max(0, deadlineAt! - Date.now()))
    listeners.add(check)
    check()
  })
}

/**
 * Waits for the images under `root` to decode, each for at most IMAGE_DECODE_TIMEOUT_MS. A broken or slow image is
 * not a failure: the page shows it that way too, and the PDF service already treats images as best effort.
 */
export function trackImageDecodes(root: ParentNode | null | undefined): void {
  if (!root || !isExportMode()) return
  root.querySelectorAll('img').forEach(img => {
    // Lazy images off screen load when the PDF service scrolls the page, after __PDF_READY__
    if (!img.getAttribute('src') || img.getAttribute('loading') === 'lazy') return
    trackExportTask('image', decodeWithin(img, IMAGE_DECODE_TIMEOUT_MS), { bestEffort: true })
  })
}

function decodeWithin(img: HTMLImageElement, ms: number): Promise<void> {
  if (typeof img.decode !== 'function') return Promise.resolve()
  return new Promise(resolve => {
    const timer = setTimeout(resolve, ms)
    img
      .decode()
      .catch(() => undefined)
      .then(() => {
        clearTimeout(timer)
        resolve()
      })
  })
}

/** Starts a new export: forgets tracked work, failures and the deadline. On entering and leaving print mode. */
export function resetExportReadiness(): void {
  pending = new Set()
  failed = new Set()
  deadlineAt = null
  listeners.forEach(listener => listener())
}
